/**
 * ContactFormModal — Replaces all mailto: links in the portal (MKTG-03)
 *
 * v1.1 - Add optional company_name field - 2026-03-24
 * v1.0 - MKTG-03: Initial implementation — replaces 6 mailto: occurrences - 2026-03-16
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <Button onClick={() => setOpen(true)}>Contact us</Button>
 *   <ContactFormModal open={open} onClose={() => setOpen(false)} />
 *
 * Optional: pass initialMessage to pre-fill the message field (e.g. connector requests).
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { portalRequestUngated } from '../services/portalRequest'; // v4.28 - G10-B: one request wrapper, carries the chosen company

interface ContactFormModalProps {
  open: boolean;
  onClose: () => void;
  initialMessage?: string;
  title?: string;
}

const ContactFormModal: React.FC<ContactFormModalProps> = ({
  open,
  onClose,
  initialMessage = '',
  title = 'Contact Us',
}) => {
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(initialMessage);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    // Reset state on close
    setName('');
    setCompanyName('');
    setEmail('');
    setMessage(initialMessage);
    setLoading(false);
    setSubmitted(false);
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await portalRequestUngated('/api/waitlist/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          company_name: companyName.trim() || undefined,
          email: email.trim().toLowerCase(),
          message: message.trim(),
          source: 'contact_form',
        }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isValid = name.trim().length > 0 && email.trim().includes('@') && message.trim().length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Close"
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {submitted ? (
          <Box sx={{ py: 2 }}>
            <Alert severity="success">
              <strong>Message sent!</strong> We'll get back to you within 1 business day.
            </Alert>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Fill in the form below and we'll get back to you within 1 business day.
            </Typography>
            <TextField
              label="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              disabled={loading}
              autoFocus
            />
            {/* Company name — optional — v1.1 */}
            <TextField
              label="Company name"
              placeholder="Acme Inc."
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              fullWidth
              disabled={loading}
            />
            <TextField
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              fullWidth
              multiline
              rows={4}
              disabled={loading}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {submitted ? (
          <Button onClick={handleClose} variant="contained" sx={{ fontWeight: 600, textTransform: 'none' }}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !isValid}
              sx={{ fontWeight: 600, textTransform: 'none', minWidth: 140 }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Send Message'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactFormModal;
